// @flow
import * as api from './api';

import { alerts, feedback } from '../../constants';
import { ATTEMPT_CREATE_RECIPE, ATTEMPT_DELETE_RECIPE, ATTEMPT_UPDATE_RECIPE, GET_RECIPES } from './constants';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { cancelAction, receiveRecipes, updateData, updateRecipe } from './actions';
import type { CreateRecipeAction, DeleteRecipeAction, UpdateRecipeAction } from './types';

import { completeSaveSchedule } from '~/core/state/ui/schedule/actions';
import { createComponentId } from '~/core/utils/schedule';
import { getConfig } from '~/core/state/config/selectors';
import { getScheduleData } from '~/core/state/ui/schedule/selectors';
import handleApi from '~/core/state/handle-api';
import handleSaga from '~/core/state/handle-saga';
import { hasSMS } from '~/core/state/entitlements/selectors';
import type { Effect } from 'redux-saga';
import { noop } from 'lodash/fp';
import { requestAlert } from '~/core/state/alerts/actions';

/*******************
 *   GET RECIPES   *
 *******************/

export function* getRecipesSaga(): Generator<noop | Effect, *, *> {
    const [recipes, error] = yield call(handleApi(api.getRecipes));
    yield put(receiveRecipes(error || recipes));
    if (error) {
        yield put(requestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR));
    }
}

function* watchGetRecipesSaga(): Generator<Effect, *, *> {
    yield takeLatest(GET_RECIPES, handleSaga(getRecipesSaga));
}

/*********************
 *   CREATE RECIPE   *
 *********************/

export function* createRecipeSaga({ payload: { newRecipe } }: CreateRecipeAction): Generator<noop | Effect, *, *> {
    const defaultSchedule = yield select(getConfig, 'recipes:defaultSchedule');
    const recipe = {
        ...newRecipe.get('data').toJS(),
        enabled: true,
        schedule: defaultSchedule.toJS(),
        scheduled: false
    };
    const [result, error] = yield call(handleApi(api.createRecipe), recipe);
    yield put(updateData(error || result));

    if (error) {
        yield put(requestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR));
    }
}

function* watchCreateRecipeSaga(): Generator<Effect, *, *> {
    yield takeEvery(ATTEMPT_CREATE_RECIPE, handleSaga(createRecipeSaga));
}

/*********************
 *   DELETE RECIPE   *
 *********************/

export function* deleteRecipeSaga({ payload: recipeId }: DeleteRecipeAction): Generator<noop | Effect, *, *> {
    const [result, error] = yield call(handleApi(api.deleteRecipe), recipeId);
    yield put(updateData(error || result));

    if (error) {
        yield put(requestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR));
    }
}

function* watchDeleteRecipeSaga(): Generator<Effect, *, *> {
    yield takeEvery(ATTEMPT_DELETE_RECIPE, handleSaga(deleteRecipeSaga));
}

/*********************
 *   UPDATE RECIPE   *
 *********************/

export function* updateRecipeSaga({ payload: recipe }: UpdateRecipeAction): Generator<Effect | noop, *, *> {
    // before to update we first need to get the schedule from the component in case the user updated it
    const componentId = createComponentId('recipe', recipe.getIn(['data', 'id']));
    const schedule = yield select(getScheduleData, { componentId });
    recipe = recipe.setIn(['data', 'schedule'], schedule);
    // if notification recipe, check sms entitlement
    if (recipe.get('isNotification', false)) {
        const hasSMSEntitlement = yield select(hasSMS);
        if (!hasSMSEntitlement && recipe.getIn(['data', 'action', 'value', 'sms'], false)) {
            // if sms is true but not entitlement then put ovewrite it as false
            recipe = recipe.setIn(['data', 'action', 'value', 'sms'], false);
        }
    }
    // now update the recipe
    const [result, error] = yield call(handleApi(api.updateRecipe), recipe.get('data').toJS());
    yield put(updateRecipe(error || result));

    if (error) {
        yield put(requestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR));
        yield put(cancelAction(recipe.getIn(['data', 'id'])));
    } else {
        yield put(completeSaveSchedule({ componentId }));
    }
}

function* watchUpdateRecipeSaga(): Generator<Effect, *, *> {
    yield takeEvery(ATTEMPT_UPDATE_RECIPE, handleSaga(updateRecipeSaga));
}

export default [watchCreateRecipeSaga, watchDeleteRecipeSaga, watchGetRecipesSaga, watchUpdateRecipeSaga];



// WEBPACK FOOTER //
// ./src/core/state/recipes/sagas.js